/**
Effect graph — per-channel effect chains, buses and a master chain, described by the song
and built from Web Audio nodes. See docs/design/format-emulation.md.

A song carries its graph as `effects` in the player meta event (song.civet's metadata), so
it saves, shares and exports with the MIDI like everything else there. Other MIDI players
ignore it and still play the notes, volume and pan.

  {
    v: 1,
    channels: { "<midi channel 0-15>": Strip },  // chain on that channel's synth output
    buses:    { "<id>": Strip },                 // shared chains, fed by sends
    master:   { chain: GraphNode[] }             // after everything, before the app's FX
  }
  Strip = { chain?: GraphNode[], sends?: [{ to: "master" | "<bus id>", gain: Param }] }
          (no sends = everything to the master at gain 1)

A Param is a number or automation: { v: <start value>, pts: [[tick, value, ramp?], …] } —
a point sets the value at that tick, or with ramp = 1 ramps linearly to it from the point
before (setValueAtTime / linearRampToValueAtTime, which is also how Online Sequencer blends
its markers). Ticks are the song's MIDI ticks, so automation follows tempo changes.

Channel chains need a synth with per-channel outputs (SpessaSynth's taps); on the legacy
SF2Synth only the master chain applies.
*/

type { MidiSong } from ./midi/song.civet
type { SynthTaps } from @danielx/lib

export type Point = [number, number, number?]
export type Param = number | { v: number, pts: Point[] }

export type GraphNode =
  | { type: "gain", gain: Param }
  | { type: "pan", pan: Param }
  | { type: "biquad", filter: BiquadFilterType, frequency: Param, Q?: Param, gain?: Param }
  /** WaveShaper, oversample none; `curve` is sample values or a named curve ("os:1".."os:10"); dry·(1−mix) + shaped·mix */
  | { type: "shaper", curve: string | number[], mix?: Param }
  /** ConvolverNode on an impulse asset (see `effectAssetLoader`) */
  | { type: "convolver", impulse: string, normalize?: boolean }
  /** Online Sequencer's delay: taps at T and 2T (gains 0.5, 0.25, the first feeding the second), T = `beats` at the current tempo */
  | { type: "osDelay", beats: number }
  /** Online Sequencer's bitcrusher (the Web Audio spec's demo worklet, quirks included) */
  | { type: "bitcrusher", depth: Param, reduction: Param }

export type Send = { to: string, gain: Param }
export type Strip = { chain?: GraphNode[], sends?: Send[] }
export type EffectGraphSpec =
  v: 1
  channels?: Record<string, Strip>
  buses?: Record<string, Strip>
  master?: { chain?: GraphNode[] }

/** Where song time sits on the context clock: context time = contextStartTime + (song seconds − playStartTime) / rate. */
export type GraphTiming =
  contextStartTime: number
  playStartTime: number
  rate: number

/**
Loads an asset a graph names (an impulse). A ref is a URL relative to the app — the public
impulses ship in www/impulses/ (see its LICENSES.md) — or `os-private:<file>`, Online
Sequencer's own impulses, which exist only in a private reference mirror and load only
where something provides them. `globalThis.effectAssetLoader(ref)` is consulted first
(render-cli installs one) and may return nothing to leave a ref to the default fetch.
*/
export effectAssets :=
  load: (ref: string): Promise<ArrayBuffer> ->
    custom := (globalThis as any).effectAssetLoader
    if custom
      data := await custom(ref)
      return data if data
    throw new Error `${ref}: a private asset, and nothing here provides it` if ref.startsWith "os-private:"
    fetch(ref) |> await |> .arrayBuffer() |> await

// ── Online Sequencer's distortion curves (kDistortData), 1025 points like getWaveShaperCurve ──
styx := (x: number) ->
  w := Math.sqrt(Math.abs x) * (0.5 + 0.5 * Math.cos(x * 30))
  0.4 * (x - 2 * Math.sign(x) * w)
shear := (x: number) ->
  z := Math.abs x
  Math.sign(x) * z * z * z * z / (z * z * z + 3e-5)
softLim := (x: number) ->
  X := Math.abs x
  return 1.5 * x if X < 0.1
  (x / X) * (0.2 * Math.tanh(1.5 * (X - 0.1) / 0.2) + 0.1 * 1.5)

OS_CURVES: Array<((x: number) => number)?> := [
  undefined
  (x: number) => 0.3 * Math.tanh(10 * x) // Limit
  (x: number) => 0.3 * ((x * 10) % 1) // Zest
  (x: number) => 0.3 * Math.sin(x * 15) // Quasar
  styx // Styx
  (x: number) => 2 * x * x // Lucid
  (x: number) => (Math.exp(2 * x) - 1) / Math.E // Tremor
  (x: number) => 0.3 * Math.sin(30 * x * x * x + x) // Zircon
  (x: number) => Math.abs(x) - 3 * Math.max(0, Math.abs(x) - 0.3) // Hornet
  shear // Shear
  softLim // SoftLim
]

curveFor := (curve: string | number[]): Float32Array ->
  if Array.isArray curve
    return new Float32Array curve
  match := /^os:(\d+)$/.exec curve
  f := match and OS_CURVES[Number match[1]]
  throw new Error `unknown curve ${curve}` unless f
  res := 512
  Float32Array.from { length: 2 * res + 1 }, (_, i) => f(i / res - 1)

// ── Online Sequencer's bitcrusher worklet, as it runs there ──
// Step 1/bitDepth (not 2^−bits), sample-and-hold by frequencyReduction read once per
// block, and phase / held value shared across channels (advancing once per channel).
BITCRUSHER_SOURCE := """
class OsBitCrusher extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [
      { name: "bitDepth", defaultValue: 12, minValue: 1, maxValue: 16 },
      { name: "frequencyReduction", defaultValue: 0.5, minValue: 0, maxValue: 1 },
    ];
  }
  constructor() { super(); this.phase_ = 0; this.last_ = 0; }
  process(inputs, outputs, parameters) {
    const input = inputs[0], output = outputs[0];
    const step = 1 / parameters.bitDepth[0], fr = parameters.frequencyReduction[0];
    for (let ch = 0; ch < input.length; ++ch) {
      const a = input[ch], o = output[ch];
      for (let i = 0; i < a.length; ++i) {
        this.phase_ += fr;
        if (this.phase_ >= 1) { this.phase_ -= 1; this.last_ = step * Math.floor(a[i] / step + 0.5); }
        o[i] = this.last_;
      }
    }
    return true;
  }
}
registerProcessor("os-bitcrusher", OsBitCrusher);
"""

bitcrusherModules := new WeakMap<BaseAudioContext, Promise<void>>()
loadBitcrusher := (context: BaseAudioContext) ->
  loaded .= bitcrusherModules.get context
  unless loaded
    url := URL.createObjectURL new Blob [BITCRUSHER_SOURCE], type: "application/javascript"
    loaded = context.audioWorklet.addModule url
    bitcrusherModules.set context, loaded
  loaded

impulseCache := new WeakMap<BaseAudioContext, Map<string, Promise<AudioBuffer>>>()
loadImpulse := (context: BaseAudioContext, ref: string) ->
  byRef .= impulseCache.get context
  unless byRef
    byRef = new Map
    impulseCache.set context, byRef
  loaded .= byRef.get ref
  unless loaded
    loaded = effectAssets.load(ref).then (data) => context.decodeAudioData data
    byRef.set ref, loaded
  loaded

type Built = { input: AudioNode, output: AudioNode }

/**
Build `spec` on `context`. `synthOut` is what the synth's `bind` returned; with `taps`
(SpessaSynth) each channel that has a strip is taken off the plain mix and through it.
Connect the returned `output` where the synth's output would have gone, and wait for
`ready` (impulses decoded, worklets registered) before an offline render starts.
*/
export buildEffectGraph := (context: BaseAudioContext, spec: EffectGraphSpec, song: MidiSong, synthOut: AudioNode, taps: SynthTaps?, timing: GraphTiming) ->
  nodes: AudioNode[] := []
  pending: Promise<unknown>[] := []
  keep := <T extends AudioNode>(n: T): T ->
    nodes.push n
    n

  tpb := song.ticksPerBeat
  toContext := (tick: number) ->
    timing.contextStartTime + (song.timeAtBeat(tick / tpb) - timing.playStartTime) / timing.rate
  start := timing.contextStartTime

  // Set a param from the song's automation, starting mid-ramp when playback starts mid-song.
  schedule := (param: AudioParam, p: Param?, map = (x: number) => x) ->
    return unless p?
    if typeof p is "number"
      param.setValueAtTime map(p), start
      return
    prevT .= -Infinity
    prevV .= p.v
    begun .= false
    for [tick, value, ramp] of p.pts
      t := toContext tick
      if t <= start
        prevT = t
        prevV = value
        continue
      unless begun
        v := if ramp and isFinite(prevT) then prevV + (value - prevV) * (start - prevT) / (t - prevT) else prevV
        param.setValueAtTime map(v), start
        begun = true
      if ramp
        param.linearRampToValueAtTime map(value), t
      else
        param.setValueAtTime map(value), t
    param.setValueAtTime map(prevV), start unless begun

  build := (n: GraphNode): Built ->
    switch n.type
      when "gain"
        g := keep context.createGain()
        schedule g.gain, n.gain
        return { input: g, output: g }
      when "pan"
        p := keep context.createStereoPanner()
        schedule p.pan, n.pan
        return { input: p, output: p }
      when "biquad"
        f := keep context.createBiquadFilter()
        f.type = n.filter
        schedule f.frequency, n.frequency
        schedule f.Q, n.Q
        schedule f.gain, n.gain
        return { input: f, output: f }
      when "shaper"
        input := keep context.createGain()
        out := keep context.createGain()
        dry := keep context.createGain()
        wet := keep context.createGain()
        ws := keep context.createWaveShaper()
        ws.curve = curveFor n.curve
        ws.oversample = "none"
        input.connect dry
        dry.connect out
        input.connect ws
        ws.connect wet
        wet.connect out
        mix := n.mix ?? 1
        schedule wet.gain, mix
        schedule dry.gain, mix, (x) => 1 - x
        return { input, output: out }
      when "convolver"
        c := keep context.createConvolver()
        // Set before the buffer: that is when a ConvolverNode reads it.
        c.normalize = n.normalize ?? true
        pending.push loadImpulse(context, n.impulse).then (buffer) => c.buffer = buffer
        return { input: c, output: c }
      when "osDelay"
        input := keep context.createGain()
        out := keep context.createGain()
        a := keep context.createDelay 10
        b := keep context.createDelay 10
        ag := keep context.createGain()
        bg := keep context.createGain()
        ag.gain.value = 0.5
        bg.gain.value = 0.25
        input.connect a
        input.connect b
        a.connect ag
        ag.connect out
        ag.connect b
        b.connect bg
        bg.connect out
        // T follows the tempo map; the tempo in force at the start is set at the start.
        for e, i of song.tempoMap
          T := n.beats * e.microsecondsPerBeat / 1e6
          t := toContext e.tick
          next := song.tempoMap[i + 1]
          continue if next and toContext(next.tick) <= start
          a.delayTime.setValueAtTime T, Math.max(t, start)
          b.delayTime.setValueAtTime 2 * T, Math.max(t, start)
        return { input, output: out }
      when "bitcrusher"
        input := keep context.createGain()
        out := keep context.createGain()
        pending.push loadBitcrusher(context).then =>
          w := keep new AudioWorkletNode context, "os-bitcrusher",
            numberOfInputs: 1
            numberOfOutputs: 1
            outputChannelCount: [2]
          schedule w.parameters.get("bitDepth")!, n.depth
          schedule w.parameters.get("frequencyReduction")!, n.reduction
          input.connect w
          w.connect out
        return { input, output: out }

  chain := (list: GraphNode[] = []): Built ->
    head := keep context.createGain()
    tail .= head as AudioNode
    for n of list
      b := build n
      tail.connect b.input
      tail = b.output
    { input: head, output: tail }

  master := chain spec.master?.chain
  masterIn := master.input
  output := master.output

  buses := new Map<string, Built>()
  for id, strip in spec.buses ?? {}
    buses.set id, chain strip.chain

  target := (to: string) -> if to is "master" then masterIn else buses.get(to)?.input

  route := (built: Built, sends: Send[]?) ->
    unless sends?.length
      built.output.connect masterIn
      return
    for s of sends
      dest := target s.to
      unless dest
        console.warn `effect graph: no bus "${s.to}"`
        continue
      g := keep context.createGain()
      schedule g.gain, s.gain
      built.output.connect g
      g.connect dest

  for id, strip in spec.buses ?? {}
    route buses.get(id)!, strip.sends

  // Channels: re-route each tap that has a strip; the rest stay on the synth's mix.
  rerouted: GainNode[] := []
  channelSpecs := Object.entries spec.channels ?? {}
  if channelSpecs.length and not taps
    console.warn "effect graph: this synth has no per-channel outputs; channel chains skipped"
  if taps
    for [id, strip] of channelSpecs
      tap := taps.channels[Number id]
      continue unless tap
      built := chain strip.chain
      tap.disconnect taps.out
      tap.connect built.input
      rerouted.push tap
      route built, strip.sends
  synthOut.connect masterIn

  output: output
  ready: Promise.all(pending).then -> undefined
  dispose: ->
    synthOut.disconnect masterIn
    for tap of rerouted
      tap.disconnect()
      tap.connect taps!.out
    for n of nodes
      n.disconnect()
    return
